// store/slices/unitSlice.ts
import { Unit, unitsApi } from "@/services/unitsApi";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

interface UnitState {
  units: Unit[];
  pagination: {
    totalItems: number;
    totalPages: number;
    currentPage: number;
    itemsPerPage: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
  selectedUnit: Unit | null;
  loading: boolean;
  error: string | null;
  updating: boolean;
}

const initialState: UnitState = {
  units: [],
  pagination: {
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
    itemsPerPage: 10,
    hasNextPage: false,
    hasPrevPage: false,
  },
  selectedUnit: null,
  loading: false,
  error: null,
  updating: false,
};

// Thunks
export const fetchUnits = createAsyncThunk(
  "units/fetchAll",
  async ({
    page,
    limit,
    search,
  }: {
    page: number;
    limit: number;
    search: string;
  }) => {
    const response = await unitsApi.getAll(page, limit, search);
    return response;
  },
);

export const fetchUnitById = createAsyncThunk(
  "units/fetchById",
  async (id: number) => {
    // Note: there's no dedicated GET /units/:id endpoint in your spec; we can fetch all and filter or implement one.
    // For simplicity, we'll fetch all and filter. Better to add a getById endpoint in backend, but here we'll simulate.
    const response = await unitsApi.getAll(1, 1000, "");
    const unit = response.units.find((u) => u.id === id);
    if (!unit) throw new Error("Unit not found");
    return unit;
  },
);

export const createUnit = createAsyncThunk(
  "units/create",
  async (data: { name: string; symbol: string }) => {
    const response = await unitsApi.create(data);
    return response.unit;
  },
);

export const updateUnit = createAsyncThunk(
  "units/update",
  async ({
    id,
    data,
  }: {
    id: number;
    data: Partial<
      Omit<Unit, "id" | "organizationId" | "createdAt" | "updatedAt">
    >;
  }) => {
    const response = await unitsApi.update(id, data);
    return response.unit;
  },
);

export const deleteUnit = createAsyncThunk(
  "units/delete",
  async (id: number) => {
    await unitsApi.delete(id);
    return id;
  },
);

const unitSlice = createSlice({
  name: "units",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetSelected: (state) => {
      state.selectedUnit = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUnits.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchUnits.fulfilled, (state, action) => {
        state.loading = false;
        state.units = action.payload.units;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchUnits.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch units";
      })
      .addCase(fetchUnitById.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchUnitById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedUnit = action.payload;
      })
      .addCase(fetchUnitById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Unit not found";
      })
      .addCase(createUnit.pending, (state) => {
        state.updating = true;
      })
      .addCase(createUnit.fulfilled, (state, action) => {
        state.updating = false;
        state.units.unshift(action.payload);
      })
      .addCase(createUnit.rejected, (state, action) => {
        state.updating = false;
        state.error = action.error.message || "Failed to create unit";
      })
      .addCase(updateUnit.pending, (state) => {
        state.updating = true;
      })
      .addCase(updateUnit.fulfilled, (state, action) => {
        state.updating = false;
        const index = state.units.findIndex((u) => u.id === action.payload.id);
        if (index !== -1) state.units[index] = action.payload;
        if (state.selectedUnit?.id === action.payload.id)
          state.selectedUnit = action.payload;
      })
      .addCase(updateUnit.rejected, (state, action) => {
        state.updating = false;
        state.error = action.error.message || "Failed to update unit";
      })
      .addCase(deleteUnit.pending, (state) => {
        state.updating = true;
      })
      .addCase(deleteUnit.fulfilled, (state, action) => {
        state.updating = false;
        state.units = state.units.filter((u) => u.id !== action.payload);
      })
      .addCase(deleteUnit.rejected, (state, action) => {
        state.updating = false;
        state.error = action.error.message || "Failed to delete unit";
      });
  },
});

export const { clearError, resetSelected } = unitSlice.actions;
export default unitSlice.reducer;
